import {
  MinusCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import {
  AutoComplete,
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
} from 'antd';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';

import {RqUserLookupAllRole} from '../../../../../ReactQuery/apis';

export const emptyMedicine = () => ({
  medicineId: null,
  medicineSource: 'INVENTORY',
  medicineName: '',
  sellingPrice: null,
  dosage: '',
  duration: '',
  beforeAfterFood: null,
  instructions: null,
  quantity: null,
  route: '',
});

export const emptyNonInventoryMedicine = () => ({
  medicineId: null,
  medicineSource: 'NON_INVENTORY',
  medicineName: '',
  sellingPrice: null,
  dosage: '',
  duration: '',
  beforeAfterFood: null,
  instructions: null,
  quantity: null,
  route: '',
});

export const emptyPanchakarma = () => ({
  therapyName: '',
  numberOfDays: null,
  sessionTime: '',
  instructions: '',
});

export const emptyDietAdvice = () => ({
  mealType: '',
  recommendedFood: '',
  avoidFood: '',
  advice: '',
});

const DOSAGE_OPTIONS = [
  '1-0-1',
  '1-1-1',
  '0-0-1',
  '2-0-2',
  '2-2-2',
  '0-0-2',
];

const PANCHAKARMA_THERAPY_OPTIONS = [
  'Snehana',
  'Swedan',
  'Basti',
  'Uttar basti',
  'Ahirodhara',
  'Udvartana',
];

const BEFORE_AFTER_FOOD_OPTIONS = [
  'After Food',
  'Before Food',
  'With Food',
  'Empty Stomach in Morning',
  'At Night',
];

export const getPersonId = person =>
  person?.id ?? person?.userId ?? person?.employeeId;

export const getPersonLabel = person =>
  person?.fullName
  || `${person?.firstName || ''} ${person?.lastName || ''}`.trim()
  || person?.email
  || person?.mobile
  || getPersonId(person);

export const getPersonHistoryId = person =>
  person?.patientHistoryId
  ?? person?.historyId
  ?? person?.medicalHistoryId
  ?? null;

const getMedicineName = item =>
  typeof item === 'string'
    ? item
    : (item?.medicineName || item?.name);

const getCatalogueSellingPrice = (
  medicineName,
  medicineNameOptions,
) => {
  if (!medicineName) {
    return null;
  }

  const match = medicineNameOptions.find(
    item => getMedicineName(item) === medicineName,
  );

  return (typeof match === 'object' && match)
    ? (match.sellingPrice ?? match.price ?? null)
    : null;
};

const filterBySearchText = (input, option) =>
  (option?.children ?? '')
    .toString()
    .toLowerCase()
    .includes(input.toLowerCase());

const currencyFormatter = value =>
  (value || value === 0)
    ? `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : '';

const currencyParser = value =>
  (value || '').toString().replace(/₹\s?|(,*)/g, '');

const PrescriptionForm = ({
  open,
  editingPrescription,
  patientOptions,
  patientsLoading,
  medicineNameOptions,
  medicinesLoading,
  confirmLoading,
  onCancel,
  onSubmit,
  token,
  form,
}) => {
  useEffect(() => {
    if (!open) {
      return;
    }

    if (editingPrescription) {
      const medicines = editingPrescription.medicines?.length
        ? editingPrescription.medicines.map(medicine => ({
          medicineId: medicine.medicineId ?? null,

          medicineSource:
            medicine.medicineSource
            || medicine.source
            || 'INVENTORY',

          medicineName:
            medicine.medicineName || '',

          sellingPrice:
            medicine.sellingPrice ?? null,

          dosage:
            medicine.dosage || '',

          duration:
            medicine.duration || '',

          beforeAfterFood:
            medicine.beforeAfterFood ?? null,

          instructions:
            medicine.instructions ?? null,

          quantity:
            medicine.quantity ?? null,

          route:
            medicine.route || '',
        }))
        : [emptyMedicine()];

      form.setFieldsValue({
        patientId: editingPrescription.patientId,
        doctorId: editingPrescription.doctorId,

        patientHistoryId:
          editingPrescription.patientHistoryId,

        doctorNotes:
          editingPrescription.doctorNotes,

        createdDate:
          editingPrescription.createdDate
            ? require('dayjs')(
              editingPrescription.createdDate,
            )
            : null,

        medicines,

        panchakarmaAdvice:
          editingPrescription.panchakarmaAdvice?.length
            ? editingPrescription.panchakarmaAdvice
            : [emptyPanchakarma()],

        dietAdvice:
          editingPrescription.dietAdvice?.length
            ? editingPrescription.dietAdvice
            : [emptyDietAdvice()],

        totalMedicinePrice:
          editingPrescription.totalMedicinePrice ?? 0,
      });
    } else {
      form.resetFields();

      form.setFieldsValue({
        medicines: [emptyMedicine()],
        panchakarmaAdvice: [emptyPanchakarma()],
        dietAdvice: [emptyDietAdvice()],
        createdDate: require('dayjs')(),
        totalMedicinePrice: 0,
      });
    }
  }, [open, editingPrescription, form]);

  const {
    data: DOCTOR = [],
    isFetching: doctorsLoading,
  } = RqUserLookupAllRole('DOCTOR', token);

  const {
    data: THERAPIST = [],
    isFetching: therapistsLoading,
  } = RqUserLookupAllRole('THERAPIST', token);

  const DOCTOR_OPTIONS = [
    ...DOCTOR.map(d => ({
      ...d,
      role: 'Doctor',
    })),
    ...THERAPIST.map(t => ({
      ...t,
      role: 'Therapist',
    })),
  ];

  const handlePatientChange = value => {
    const selectedPatient = patientOptions.find(
      p => getPersonId(p) === value,
    );

    form.setFieldsValue({
      patientHistoryId:
        getPersonHistoryId(selectedPatient),
    });
  };

  const medicinesWatch =
    Form.useWatch('medicines', form) || [];

  useEffect(() => {
    if (!open) {
      return;
    }

    const total = medicinesWatch.reduce(
      (sum, medicine) => {
        const qty =
          Number(medicine?.quantity) || 0;

        const price =
          Number(medicine?.sellingPrice) || 0;

        return sum + (qty * price);
      },
      0,
    );

    form.setFieldValue(
      'totalMedicinePrice',
      total,
    );
  }, [
    JSON.stringify(medicinesWatch),
    open,
  ]);

  const handleMedicineNameChange = (
    name,
    value,
    isNonInventory,
  ) => {
    const selectedMedicine =
      medicineNameOptions.find(
        item =>
          getMedicineName(item) === value,
      );

    const suggestedPrice =
      getCatalogueSellingPrice(
        value,
        medicineNameOptions,
      );

    const medicines =
      form.getFieldValue('medicines') || [];

    medicines[name] = {
      ...medicines[name],

      medicineId:
        selectedMedicine?.medicineId
        ?? selectedMedicine?.id
        ?? null,

      medicineSource: isNonInventory
        ? 'NON_INVENTORY'
        : 'INVENTORY',

      medicineName: value,

      sellingPrice:
        medicines[name]?.sellingPrice == null
          ? suggestedPrice
          : medicines[name]?.sellingPrice,
    };

    form.setFieldValue(
      'medicines',
      medicines,
    );
  };

  const handleAddMedicine = (
    source,
    add,
  ) => {
    if (source === 'INVENTORY') {
      add(emptyMedicine());
      return;
    }

    add(emptyNonInventoryMedicine());
  };

  return (
    <Modal
      title={(
        <span className={'text-primary-700 font-bold text-base'}>
          {editingPrescription
            ? 'Edit Prescription'
            : 'Add Prescription'}
        </span>
      )}
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      confirmLoading={confirmLoading}
      width={800}
      okText={
        editingPrescription
          ? 'Update'
          : 'Add'
      }
      okButtonProps={{
        className: 'custom-modal-btn',
      }}
      destroyOnHidden
    >
      <Form
        form={form}
        layout={'vertical'}
        className={'mt-4'}
      >
        <div
          className={
            'grid grid-cols-2 gap-x-4'
          }
        >
          <Form.Item
            name={'patientId'}
            label={'Patient'}
            rules={[
              {
                required: true,
                message: 'Patient is required',
              },
            ]}
          >
            <Select
              showSearch
              placeholder={'Select patient'}
              loading={patientsLoading}
              notFoundContent={
                patientsLoading
                  ? 'Loading…'
                  : 'No patients found'
              }
              optionFilterProp={'children'}
              filterOption={filterBySearchText}
              onChange={handlePatientChange}
            >
              {patientOptions.map(patient => (
                <Select.Option
                  key={getPersonId(patient)}
                  value={getPersonId(patient)}
                >
                  {getPersonLabel(patient)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={'doctorId'}
            label={'Doctor / Therapist'}
            rules={[
              {
                required: true,
                message:
                  'Doctor or Therapist is required',
              },
            ]}
          >
            <Select
              showSearch
              placeholder={
                'Select Doctor / Therapist'
              }
              loading={
                doctorsLoading
                || therapistsLoading
              }
              notFoundContent={
                (
                  doctorsLoading
                  || therapistsLoading
                )
                  ? 'Loading…'
                  : 'No doctors or therapists found'
              }
              optionFilterProp={'children'}
              filterOption={filterBySearchText}
            >
              {DOCTOR_OPTIONS.map(person => (
                <Select.Option
                  key={getPersonId(person)}
                  value={getPersonId(person)}
                >
                  {`${getPersonLabel(person)} — ${person.role}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={'createdDate'}
            label={'Created Date'}
            rules={[
              {
                required: true,
                message: 'Date is required',
              },
            ]}
          >
            <DatePicker
              className={'w-full'}
              format={'YYYY-MM-DD'}
            />
          </Form.Item>
        </div>

        <Form.Item
          name={'doctorNotes'}
          label={'Doctor Notes'}
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        {/* MEDICINES */}
        <Form.List name={'medicines'}>
          {(fields, {add, remove}) => (
            <>
              <div
                className={
                  'flex justify-between items-center mb-2'
                }
              >
                <span
                  className={
                    'font-semibold text-gray-700'
                  }
                >
                  Medicines
                </span>

                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'inventory',
                        label: 'Medicine from Stock',
                      },
                      {
                        key: 'nonInventory',
                        label:
                          'External / Non-Stock Medicine',
                      },
                    ],
                    onClick: ({key}) => {
                      handleAddMedicine(
                        key === 'inventory'
                          ? 'INVENTORY'
                          : 'NON_INVENTORY',
                        add,
                      );
                    },
                  }}
                  trigger={['click']}
                >
                  <Button
                    size={'small'}
                    icon={
                      <PlusCircleOutlined />
                    }
                  >
                    Add Internal / External Medicine
                  </Button>
                </Dropdown>
              </div>

              {fields.map(
                ({
                  key,
                  name,
                  ...rest
                }) => {
                  const medicine =
                    medicinesWatch[name] || {};

                  const isNonInventory =
                    medicine.medicineSource
                    === 'NON_INVENTORY';

                  return (
                    <div
                      key={key}
                      className={
                        'border border-primary-100 rounded-lg p-3 mb-3 grid grid-cols-2 gap-x-4'
                      }
                    >
                      {/* SOURCE */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'medicineSource',
                        ]}
                        hidden
                      >
                        <Input />
                      </Form.Item>

                      {/* MEDICINE ID */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'medicineId',
                        ]}
                        hidden
                      >
                        <Input />
                      </Form.Item>

                      {/* MEDICINE NAME */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'medicineName',
                        ]}
                        label={
                          isNonInventory
                            ? 'External Medicine Name'
                            : 'Medicine Name'
                        }
                        rules={[
                          {
                            required: true,
                            message: 'Required',
                          },
                        ]}
                      >
                        <AutoComplete
                          showSearch
                          allowClear
                          placeholder={
                            isNonInventory
                              ? 'Type or select external medicine'
                              : 'Type or select medicine'
                          }
                          loading={
                            medicinesLoading
                          }
                          options={
                            medicineNameOptions.map(
                              item => {
                                const value =
                                  getMedicineName(
                                    item,
                                  );

                                return {
                                  value,
                                  label: value,
                                };
                              },
                            )
                          }
                          filterOption={(
                            inputValue,
                            option,
                          ) =>
                            option?.value
                              ?.toLowerCase()
                              .includes(
                                inputValue.toLowerCase(),
                              )
                          }
                          onSelect={value =>
                            handleMedicineNameChange(
                              name,
                              value,
                              isNonInventory,
                            )
                          }
                          onChange={value =>
                            handleMedicineNameChange(
                              name,
                              value,
                              isNonInventory,
                            )
                          }
                        />
                      </Form.Item>

                      {/* SELLING PRICE */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'sellingPrice',
                        ]}
                        label={'Selling Price (₹)'}
                        rules={[
                          {
                            required: true,
                            message: 'Required',
                          },
                        ]}
                      >
                        <InputNumber
                          min={0}
                          className={'w-full'}
                          formatter={
                            currencyFormatter
                          }
                          parser={
                            currencyParser
                          }
                        />
                      </Form.Item>

                      {/* DOSAGE */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'dosage',
                        ]}
                        label={'Dosage'}
                      >
                        <Select
                          allowClear
                          placeholder={
                            'Select dosage'
                          }
                        >
                          {DOSAGE_OPTIONS.map(
                            option => (
                              <Select.Option
                                key={option}
                                value={option}
                              >
                                {option}
                              </Select.Option>
                            ),
                          )}
                        </Select>
                      </Form.Item>

                      {/* DURATION */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'duration',
                        ]}
                        label={'Duration'}
                      >
                        <Input
                          placeholder={'e.g. 5 Days'}
                        />
                      </Form.Item>

                      {/* BEFORE / AFTER FOOD */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'beforeAfterFood',
                        ]}
                        label={'Before/After Food'}
                      >
                        <Select
                          allowClear
                          placeholder={'Select'}
                        >
                          {BEFORE_AFTER_FOOD_OPTIONS.map(
                            option => (
                              <Select.Option
                                key={option}
                                value={option}
                              >
                                {option}
                              </Select.Option>
                            ),
                          )}
                        </Select>
                      </Form.Item>

                      {/* ROUTE */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'route',
                        ]}
                        label={'Route'}
                      >
                        <Input
                          placeholder={'e.g. Oral'}
                        />
                      </Form.Item>

                      {/* QUANTITY */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'quantity',
                        ]}
                        label={'Quantity'}
                      >
                        <InputNumber
                          min={0}
                          className={'w-full'}
                        />
                      </Form.Item>

                      {/* INSTRUCTIONS */}
                      <Form.Item
                        {...rest}
                        name={[
                          name,
                          'instructions',
                        ]}
                        label={'Instructions'}
                      >
                        <Input />
                      </Form.Item>

                      {fields.length > 1 && (
                        <Button
                          danger
                          type={'text'}
                          icon={
                            <MinusCircleOutlined />
                          }
                          onClick={() =>
                            remove(name)
                          }
                          className={
                            'col-span-2 justify-self-end'
                          }
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  );
                },
              )}
            </>
          )}
        </Form.List>

        {/* TOTAL */}
        <Form.Item
          name={'totalMedicinePrice'}
          label={'Total Medicine Price (₹)'}
        >
          <InputNumber
            disabled
            className={'w-full font-semibold'}
            formatter={currencyFormatter}
            parser={currencyParser}
          />
        </Form.Item>

        {/* PANCHAKARMA */}
        <Form.List name={'panchakarmaAdvice'}>
          {(fields, {add, remove}) => (
            <>
              <div
                className={
                  'flex justify-between items-center mb-2 mt-4'
                }
              >
                <span
                  className={
                    'font-semibold text-gray-700'
                  }
                >
                  Panchakarma Advice
                </span>

                <Button
                  size={'small'}
                  icon={
                    <PlusCircleOutlined />
                  }
                  onClick={() =>
                    add(emptyPanchakarma())
                  }
                >
                  Add Therapy
                </Button>
              </div>

              {fields.map(
                ({
                  key,
                  name,
                  ...rest
                }) => (
                  <div
                    key={key}
                    className={
                      'border border-primary-100 rounded-lg p-3 mb-3 grid grid-cols-2 gap-x-4'
                    }
                  >
                    <Form.Item
                      {...rest}
                      name={[
                        name,
                        'therapyName',
                      ]}
                      label={'Therapy Name'}
                    >
                      <Select
                        allowClear
                        placeholder={
                          'Select therapy'
                        }
                      >
                        {PANCHAKARMA_THERAPY_OPTIONS.map(
                          option => (
                            <Select.Option
                              key={option}
                              value={option}
                            >
                              {option}
                            </Select.Option>
                          ),
                        )}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[
                        name,
                        'numberOfDays',
                      ]}
                      label={'Number of Days'}
                    >
                      <InputNumber
                        min={0}
                        className={'w-full'}
                      />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[
                        name,
                        'sessionTime',
                      ]}
                      label={'Session Time'}
                    >
                      <Input
                        placeholder={
                          'e.g. 09:00 AM'
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[
                        name,
                        'instructions',
                      ]}
                      label={'Instructions'}
                    >
                      <Input />
                    </Form.Item>

                    {fields.length > 1 && (
                      <Button
                        danger
                        type={'text'}
                        icon={
                          <MinusCircleOutlined />
                        }
                        onClick={() =>
                          remove(name)
                        }
                        className={
                          'col-span-2 justify-self-end'
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ),
              )}
            </>
          )}
        </Form.List>

        {/* DIET */}
        <Form.List name={'dietAdvice'}>
          {(fields, {add, remove}) => (
            <>
              <div
                className={
                  'flex justify-between items-center mb-2 mt-4'
                }
              >
                <span
                  className={
                    'font-semibold text-gray-700'
                  }
                >
                  Diet Advice
                </span>

                <Button
                  size={'small'}
                  icon={
                    <PlusCircleOutlined />
                  }
                  onClick={() =>
                    add(emptyDietAdvice())
                  }
                >
                  Add Meal
                </Button>
              </div>

              {fields.map(
                ({
                  key,
                  name,
                  ...rest
                }) => (
                  <div
                    key={key}
                    className={
                      'border border-primary-100 rounded-lg p-3 mb-3 grid grid-cols-2 gap-x-4'
                    }
                  >
                    <Form.Item
                      {...rest}
                      name={[
                        name,
                        'mealType',
                      ]}
                      label={'Meal Type'}
                    >
                      <Select placeholder={'Select meal'}>
                        <Select.Option value={'Breakfast'}>
                          Breakfast
                        </Select.Option>
                        <Select.Option value={'Lunch'}>
                          Lunch
                        </Select.Option>
                        <Select.Option value={'Dinner'}>
                          Dinner
                        </Select.Option>
                        <Select.Option value={'Snacks'}>
                          Snacks
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[
                        name,
                        'recommendedFood',
                      ]}
                      label={'Recommended Food'}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[
                        name,
                        'avoidFood',
                      ]}
                      label={'Avoid Food'}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[
                        name,
                        'advice',
                      ]}
                      label={'Advice'}
                    >
                      <Input />
                    </Form.Item>

                    {fields.length > 1 && (
                      <Button
                        danger
                        type={'text'}
                        icon={
                          <MinusCircleOutlined />
                        }
                        onClick={() =>
                          remove(name)
                        }
                        className={
                          'col-span-2 justify-self-end'
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ),
              )}
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

PrescriptionForm.propTypes = {
  open: PropTypes.bool,
  editingPrescription: PropTypes.object,
  patientOptions: PropTypes.array,
  patientsLoading: PropTypes.bool,
  doctorOptions: PropTypes.array,
  doctorsLoading: PropTypes.bool,
  medicineNameOptions: PropTypes.array,
  medicinesLoading: PropTypes.bool,
  confirmLoading: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  form: PropTypes.object,
  token: PropTypes.string,
};

export default PrescriptionForm;
